import React, { forwardRef } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ChecklistPage,
  DocHeader,
  DocFooter,
  SectionTitle,
  SubsectionTitle,
  ObjectiveBox,
  TimeLimit,
  BoldLabel,
  CheckList,
  PresentationRow,
  PanelTitle,
  TextWithDeadlineChips,
} from "./ChecklistUi";
import {
  checklistRootSx,
  panelGridSx,
  acceptablePanelSx,
  rejectPanelSx,
  metaFinalSx,
  qualityGridSx,
} from "./checklistStyles";

const NS = "garmentProdChecklist";
const arr = (t, key) => t(`${NS}.${key}`, { returnObjects: true }) || [];

const GarmentProdCheckListDocument = forwardRef(function GarmentProdCheckListDocument(
  _props,
  ref,
) {
  const { t } = useTranslation();

  return (
    <Box ref={ref} className="garment-checklist-root" sx={checklistRootSx}>
      {/* Página 1 */}
      <ChecklistPage>
        <DocHeader t={t} />
        <SectionTitle sectionKey="s1">{t(`${NS}.s1.title`)}</SectionTitle>
        <ObjectiveBox
          t={t}
          labelKey="labelObjective"
          text={t(`${NS}.s1.objective`)}
        />
        <CheckList
          items={[t(`${NS}.s1.sourcingIntro`)]}
          nestedMap={{ 0: arr(t, "s1.sourcingNested") }}
        />
        <TimeLimit>{t(`${NS}.s1.timeLimit`)}</TimeLimit>
        <BoldLabel>{t(`${NS}.s1.sheetLabel`)}</BoldLabel>
        <CheckList items={arr(t, "s1.sheetItems")} />

        <SectionTitle sectionKey="s2">{t(`${NS}.s2.title`)}</SectionTitle>
        <ObjectiveBox
          t={t}
          labelKey="labelObjective"
          text={t(`${NS}.s2.objective`)}
        />
        <CheckList
          items={[
            ...arr(t, "s2.items"),
            t(`${NS}.s2.materialsIntro`),
            t(`${NS}.s2.prototypeIntro`),
          ]}
          nestedMap={{
            [arr(t, "s2.items").length]: arr(t, "s2.materialsNested"),
            [arr(t, "s2.items").length + 1]: arr(t, "s2.prototypeNested"),
          }}
        />
        <DocFooter t={t} page={1} />
      </ChecklistPage>

      {/* Página 2 */}
      <ChecklistPage>
        <DocHeader t={t} />
        <CheckList items={arr(t, "page2.samplingTail")} />
        <SectionTitle sectionKey="s3">{t(`${NS}.s3.title`)}</SectionTitle>
        <ObjectiveBox
          t={t}
          labelKey="labelObjective"
          text={t(`${NS}.s3.objective`)}
        />
        <SubsectionTitle>{t(`${NS}.s3.smsTitle`)}</SubsectionTitle>
        <CheckList items={arr(t, "s3.smsItems")} />
        <SubsectionTitle>{t(`${NS}.s3.bulkTitle`)}</SubsectionTitle>
        <CheckList items={arr(t, "s3.bulkItems")} />
        <DocFooter t={t} page={2} />
      </ChecklistPage>

      {/* Página 3 */}
      <ChecklistPage>
        <DocHeader t={t} />
        <CheckList items={[t(`${NS}.page3.prodMeeting`)]} />
        <CheckList
          items={[t(`${NS}.page3.inspectionIntro`)]}
          nestedMap={{ 0: arr(t, "page3.inspectionNested") }}
        />
        <CheckList
          items={[
            t(`${NS}.page3.sealedSamples`),
            t(`${NS}.page3.topSamples`),
            t(`${NS}.page3.finalAudit`),
            t(`${NS}.page3.docsIntro`),
          ]}
          nestedMap={{ 3: arr(t, "page3.docsNested") }}
        />
        <SectionTitle sectionKey="s4">{t(`${NS}.page3.s4Title`)}</SectionTitle>
        <ObjectiveBox
          t={t}
          labelKey="labelInstructions"
          text={t(`${NS}.page3.s4Instructions`)}
        />
        <Box sx={qualityGridSx}>
          <CheckList items={arr(t, "page3.qualityCol1")} />
          <CheckList items={arr(t, "page3.qualityCol2")} />
        </Box>
        <SectionTitle sectionKey="s5">{t(`${NS}.page3.s5Title`)}</SectionTitle>
        <ObjectiveBox
          t={t}
          labelKey="labelObjective"
          text={t(`${NS}.page3.s5Objective`)}
        />
        <BoldLabel>{t(`${NS}.page3.photoLabel`)}</BoldLabel>
        <CheckList items={arr(t, "page3.photoItems")} />
        <DocFooter t={t} page={3} />
      </ChecklistPage>

      {/* Página 4 */}
      <ChecklistPage>
        <DocHeader t={t} />
        <CheckList items={[t(`${NS}.page4.attachment`)]} />
        <Box sx={panelGridSx}>
          <Box sx={acceptablePanelSx}>
            <PanelTitle variant="ok">
              {t(`${NS}.page4.acceptableTitle`)}
            </PanelTitle>
            {arr(t, "page4.acceptableItems").map((line, i) => (
              <PresentationRow key={i} text={line} mark="✓" variant="ok" />
            ))}
          </Box>
          <Box sx={rejectPanelSx}>
            <PanelTitle variant="reject">
              {t(`${NS}.page4.rejectTitle`)}
            </PanelTitle>
            {arr(t, "page4.rejectItems").map((line, i) => (
              <PresentationRow key={i} text={line} mark="✕" variant="reject" />
            ))}
          </Box>
        </Box>
        <Box sx={metaFinalSx}>
          <strong>{t(`${NS}.labelFinalGoal`)}</strong>{" "}
          <TextWithDeadlineChips text={t(`${NS}.page4.finalGoal`)} />
        </Box>
        <DocFooter t={t} page={4} />
      </ChecklistPage>
    </Box>
  );
});

export default GarmentProdCheckListDocument;
